import React, { createContext, useState, useEffect, ReactNode } from 'react';

// Define the context value type with both label and id for selectedFacility
type FacilityContextType = {
  selectedFacility: { label: string; id: number };  // updated structure
  setSelectedFacility: React.Dispatch<React.SetStateAction<{ label: string; id: number }>>;  // updated setter
};

// Create the context
export const FacilityContext = createContext<FacilityContextType | undefined>(undefined);

// Create the Provider
interface FacilityProviderProps {
  children: ReactNode;
}

export const FacilityProvider: React.FC<FacilityProviderProps> = ({ children }) => {
  // Initialize the selectedFacility state with label and id
  const [selectedFacility, setSelectedFacility] = useState<{ label: string; id: number }>({ label: '', id: 0 });

  // Restore selected facility id from sessionStorage (stored as a numeric string site-wide).
  // Label is filled in later by Header once facility options load.
  useEffect(() => {
    const raw = sessionStorage.getItem('matchedFacilityId');
    if (!raw) return;

    let id: number | null = null;
    let label = '';

    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed) && 'id' in parsed) {
        const o = parsed as { id: unknown; label?: unknown };
        id = Number(o.id);
        label = o.label != null ? String(o.label) : '';
      } else if (typeof parsed === 'number' && !Number.isNaN(parsed)) {
        id = parsed;
      }
    } catch {
      // Plain id string, not JSON
    }

    if (id == null || Number.isNaN(id) || id <= 0) {
      const parsedId = parseInt(raw, 10);
      if (!Number.isNaN(parsedId) && parsedId > 0) {
        id = parsedId;
      }
    }

    if (id != null && !Number.isNaN(id) && id > 0) {
      setSelectedFacility({ id, label });
    }
  }, []);

  return (
    <FacilityContext.Provider value={{ selectedFacility, setSelectedFacility }}>
      {children}
    </FacilityContext.Provider>
  );
};
